/**
 * Localized copy for the AutoMapa WhatsApp widget.
 * Uses Client View intl catalogs with Spanish fallbacks.
 */
import { useMemo } from "react";
import { useLocale, useOptionalLocale } from "../../intl";
import { useOptionalCurrency } from "../../intl/CurrencyProvider.jsx";

const FALLBACK = Object.freeze({
  assistantOnline: "Asistente en línea",
  openChatAria: "Abrir chat de WhatsApp de AutoMapa",
  closeChatAria: "Cerrar chat de WhatsApp",
  viewingVehicle: "Vehículo",
  viewListing: "Ver publicación",
  clearVehicle: "Quitar vehículo",
  recentVehicle: "Continuamos con el vehículo que estabas consultando.",
  statusEyebrow: "Estado de tu consulta",
  referenceLabel: "Referencia",
  greetingTitle: "Hola, soy el asistente de AutoMapa.",
  greetingWithVehicle:
    "Te ayudo con este vehículo. Para enviar tu consulta, necesito algunos datos rápidos.",
  greetingWithoutVehicle:
    "Hola, soy el asistente de AutoMapa. Te ayudo a consultar vehículos o revisar opciones de financiación.",
  startIntake: "Empezar consulta",
  continueDraft: "Continuar consulta",
  preferChat: "Prefiero chatear",
  useForm: "Usar formulario",
  continueWhatsapp: "Continuar en WhatsApp",
  clearDraft: "Borrar datos de esta consulta",
  back: "Atrás",
  skip: "Omitir",
  next: "Continuar",
  submit: "Enviar consulta",
  newInquiry: "Empezar nueva consulta",
  retry: "Reintentar",
  returnReview: "Volver",
  promptName: "¿Cuál es tu nombre?",
  promptWhatsapp: "¿Cuál es tu número de WhatsApp?",
  promptEmail: "¿Quieres dejar un correo? Es opcional.",
  promptFinancing: "¿Te interesa financiar este vehículo?",
  promptDownPayment: "¿Cuánto podrías dar de cuota inicial?",
  promptLoanTerm: "¿Qué plazo prefieres?",
  promptNotes: "¿Quieres agregar algún detalle para el asesor?",
  promptReview: "Revisa tu consulta antes de enviarla.",
  placeholderName: "Tu nombre",
  placeholderWhatsapp: "Ej. 300 123 4567",
  placeholderEmail: "correo@ejemplo.com",
  placeholderDownPayment: "Ej. 10000000",
  placeholderNotes: "Opcional",
  labelName: "Nombre",
  labelWhatsapp: "WhatsApp",
  labelEmail: "Correo electrónico",
  labelDownPayment: "Cuota inicial",
  labelLoanTerm: "Plazo del crédito",
  labelNotes: "Notas para el asesor",
  labelFinancing: "Financiación",
  labelVehicle: "Vehículo",
  labelTermMonths: "{months} meses",
  financingYes: "Sí",
  financingNo: "No",
  financingUnsure: "No estoy seguro",
  consentIntro:
    "Al enviar esta consulta aceptas la Política de privacidad y los Términos y condiciones de AutoMapa, incluido el contacto por WhatsApp y el envío de tu solicitud al concesionario o asesor correspondiente.",
  consentCheckbox:
    "He leído y acepto la Política de privacidad y los Términos y condiciones.",
  consentRequired: "Necesitamos tu autorización para enviar la consulta.",
  privacyPolicyLink: "Política de privacidad",
  termsLink: "Términos y condiciones",
  legalLinksSeparator: "·",
  continueWhatsappNote:
    "Al continuar en WhatsApp, abres una conversación directa con AutoMapa desde tu teléfono.",
  submitting: "Enviando tu consulta…",
  submittingHint: "Esto solo toma un momento.",
  success: "Recibimos tu consulta. AutoMapa continuará por WhatsApp.",
  error: "No pudimos enviar la consulta. Intenta de nuevo o continúa en WhatsApp.",
  draftRestore: "Encontramos una consulta en progreso. Puedes continuar donde la dejaste.",
  recentStatus: "Ya recibimos una consulta reciente para este vehículo.",
  prefillHelper: "Usamos tus datos guardados para ahorrar tiempo.",
  vehicleSwitched: "Actualizamos la consulta al vehículo que estás viendo.",
  dismiss: "Cerrar",
  chatIntro: "Tu consulta está conectada a AutoMapa.",
  chatBotIntro: "Puedes responder aquí. El asistente te pedirá solo los datos que falten.",
  chatSyncHelper: "La conversación se actualiza con tus mensajes de WhatsApp.",
  chatDelayHelper: "Los mensajes de WhatsApp pueden tardar unos segundos en aparecer.",
  chatContinueFallback:
    "Si no ves la respuesta aquí, mantén este chat abierto: los mensajes de WhatsApp se sincronizan solos.",
  chatFormFallback: "También puedes completar el formulario rápido.",
  chatPlaceholder: "Escribe un mensaje…",
  chatSend: "Enviar",
  chatSending: "Enviando…",
  chatStoredNotice:
    "Mensaje guardado. Lo sincronizaremos con WhatsApp cuando la conversación esté activa en tu teléfono.",
  chatSyncPendingNotice:
    "Mensaje guardado. Lo sincronizaremos con WhatsApp cuando la conversación esté activa en tu teléfono.",
  chatOpenSentNotice:
    "Te enviamos un WhatsApp para activar la conversación. Responde allí; luego los mensajes se sincronizan aquí y en tu teléfono.",
  chatConsentIntro:
    "Listo. Revisa y autoriza el envío de tu consulta para conectar con un asesor.",
  chatLoadError: "No pudimos cargar la conversación. Intenta de nuevo.",
  chatSendError: "No pudimos guardar el mensaje. Intenta de nuevo.",
  chatEmptyBody: "Escribe un mensaje para enviar.",
  chatLoading: "Cargando conversación…",
  channelWhatsapp: "WhatsApp",
  channelWeb: "Web",
  channelBot: "AutoMapa",
  statusRefresh: "Actualizar estado",
  validationNameRequired: "Por favor escribe tu nombre.",
  validationNameShort: "El nombre debe tener al menos 2 caracteres.",
  validationWhatsappRequired: "Por favor escribe tu número de WhatsApp.",
  validationWhatsappInvalid: "Ingresa un número válido (Colombia o internacional).",
  validationEmailInvalid: "El correo no parece válido.",
  validationFinancingRequired: "Selecciona una opción.",
  validationDownPaymentInvalid: "Ingresa un monto válido.",
  validationDownPaymentNegative: "La cuota inicial debe ser un número mayor o igual a 0.",
  validationLoanTermInvalid: "Selecciona un plazo válido.",
  handoffSuccess:
    "Hola AutoMapa, ya envié mi consulta desde la web y quiero continuar por WhatsApp.",
  handoffError:
    "Hola AutoMapa, intenté enviar mi consulta desde la web y quiero continuar por WhatsApp.",
  handoffVehicleInterest: "Hola AutoMapa, estoy interesado en este vehículo:",
  handoffGeneral: "Hola AutoMapa, quiero recibir ayuda para encontrar o consultar un vehículo.",
  handoffPrice: "Precio",
  handoffLocation: "Ubicación",
  handoffListing: "Publicación",
  handoffMyDetails: "Mis datos",
  handoffFinancing: "Financiación",
  handoffInterest: "Interés",
  handoffDownPayment: "Cuota inicial",
  handoffTerm: "Plazo preferido",
  handoffNotes: "Notas",
  handoffReference: "Referencia AutoMapa",
  handoffTermMonths: "{months} meses",
  statusSubmittedTitle: "Consulta recibida",
  statusSubmittedDescription: "Recibimos tu consulta en AutoMapa.",
  statusBrokerSubmittedTitle: "Consulta enviada por WhatsApp",
  statusBrokerSubmittedDescription:
    "Enviamos tu solicitud al flujo de WhatsApp de AutoMapa.",
  statusConfirmingTitle: "Confirmando tu interés",
  statusConfirmingDescription: "Estamos confirmando los detalles para continuar.",
  statusWaitingForDealerTitle: "Buscando respuesta del concesionario",
  statusWaitingForDealerDescription: "Estamos esperando respuesta del concesionario.",
  statusDealerAcceptedTitle: "Concesionario disponible",
  statusDealerAcceptedDescription: "El concesionario está disponible para ayudarte.",
  statusHandoffCompleteTitle: "Contacto compartido",
  statusHandoffCompleteDescription: "Ya puedes continuar la conversación por WhatsApp.",
  statusPartialSuccessTitle: "Consulta guardada, WhatsApp pendiente",
  statusPartialSuccessDescription:
    "Guardamos tu consulta, pero hubo un problema al iniciar el flujo de WhatsApp.",
  statusFailedTitle: "No pudimos enviar la consulta",
  statusFailedDescription:
    "No pudimos completar el envío. Puedes intentar de nuevo o continuar directamente en WhatsApp.",
  statusSubmittingTitle: "Enviando tu consulta…",
  statusSubmittingDescription: "Esto solo toma un momento.",
  privacyAccountPreferencesApply:
    "Tus preferencias de privacidad y notificaciones de AutoMapa se aplican a este chat.",
  privacyReviewSettings: "Revisar privacidad y notificaciones.",
  privacyReviewRequired:
    "Antes de que AutoMapa pueda guardar o enviar esta consulta, revisa tus acuerdos de privacidad y cuenta.",
  privacyRequiredBeforeLead:
    "Antes de que AutoMapa pueda guardar o enviar esta consulta, revisa tus acuerdos de privacidad y cuenta.",
  privacyContinueChatting: "Seguir chateando",
  privacyDealerSharingDisabled:
    "AutoMapa puede seguir ayudándote en el chat, pero no puede compartir tu contacto con un vendedor hasta que autorices el intercambio con concesionarios.",
  privacyHistoryNotSaved:
    "Esta conversación no se guarda en tu cuenta hasta que revises tu configuración de privacidad.",
  privacySettingsUpdated: "Tu configuración de privacidad está actualizada. ¿Continuar con el envío de esta consulta?",
  privacyContinuePendingAction: "Continuar",
  privacyComplianceUnavailable:
    "No pudimos verificar tu configuración de privacidad. Puedes seguir con preguntas generales, pero las consultas guardadas no están disponibles temporalmente.",
  privacyRetry: "Reintentar",
  privacyReturnToChat: "Volver al chat",
  privacyReviewTitle: "Revisión requerida",
  privacyNotNow: "Ahora no",
  guestPrivacyNotice:
    "Al enviar un mensaje, autorizas a AutoMapa a tratar la información que proporcionas para responder a tu solicitud. Consulta nuestro Aviso de Privacidad y los Términos de Uso.",
  guestLeadTitle: "Guardar esta consulta",
  guestLeadNotice:
    "AutoMapa guardará la información de contacto y los detalles de la consulta que proporciones para responder y gestionar tu solicitud. Tu contacto no se compartirá con un vendedor hasta que autorices esa conexión.",
  guestContinueAsGuest: "Continuar como invitado",
  guestCreateAccount: "Crear cuenta",
  guestCancel: "Cancelar",
  guestCreateAccountTitle: "Crea una cuenta AutoMapa",
  guestCreateAccountDescription:
    "Crea una cuenta AutoMapa para guardar esta conversación, gestionar consultas, controlar el intercambio con concesionarios y elegir cómo recibes actualizaciones.",
  guestEmailLabel: "Correo electrónico",
  guestContinueButton: "Continuar",
  guestCheckEmailTitle: "Revisa tu correo",
  guestCheckEmailDescription:
    "Revisa tu correo para continuar de forma segura. Si ya existe una cuenta AutoMapa, el mensaje te ayudará a iniciar sesión. Si no, te ayudará a crear una.",
  guestNewAccountAgreementsTitle: "Acepta los acuerdos requeridos",
  guestTermsAcceptance: "Acepto los Términos de Uso de AutoMapa.",
  guestDataAuthorization:
    "Autorizo a AutoMapa a tratar mis datos personales para crear y gestionar mi cuenta, guardar conversaciones y consultas, prevenir fraude y prestar los servicios que solicito, según la Política de Privacidad.",
  guestExistingAccountTitle: "Cuenta encontrada",
  guestExistingAccountDescription:
    "Este correo está vinculado a una cuenta AutoMapa. Inicia sesión de forma segura para adjuntar esta conversación.",
  guestSignIn: "Iniciar sesión",
  guestPasswordLabel: "Crea una contraseña",
  guestConsentFailed: "No pudimos registrar tu autorización. Intenta de nuevo.",
});

/**
 * @param {(key: string, params?: object) => string} t
 */
export function buildAutomapWhatsappWidgetCopy(t) {
  const pick = (key, fallback, params) => {
    try {
      const value = t(`forms.automapWhatsapp.${key}`, params);
      if (!value || value === `forms.automapWhatsapp.${key}`) return fallback;
      return value;
    } catch {
      return fallback;
    }
  };

  return {
    assistantOnline: pick("assistantOnline", FALLBACK.assistantOnline),
    openChatAria: pick("openChatAria", FALLBACK.openChatAria),
    closeChatAria: pick("closeChatAria", FALLBACK.closeChatAria),
    viewingVehicle: pick("viewingVehicle", FALLBACK.viewingVehicle),
    viewListing: pick("viewListing", FALLBACK.viewListing),
    clearVehicle: pick("clearVehicle", FALLBACK.clearVehicle),
    recentVehicle: pick("recentVehicle", FALLBACK.recentVehicle),
    statusEyebrow: pick("statusEyebrow", FALLBACK.statusEyebrow),
    referenceLabel: pick("referenceLabel", FALLBACK.referenceLabel),
    greetingTitle: pick("greetingTitle", FALLBACK.greetingTitle),
    greetingWithVehicle: pick("greetingWithVehicle", FALLBACK.greetingWithVehicle),
    greetingWithoutVehicle: pick("greetingWithoutVehicle", FALLBACK.greetingWithoutVehicle),
    startIntake: pick("startIntake", FALLBACK.startIntake),
    continueDraft: pick("continueDraft", FALLBACK.continueDraft),
    preferChat: pick("preferChat", FALLBACK.preferChat),
    useForm: pick("useForm", FALLBACK.useForm),
    continueWhatsapp: pick("continueWhatsapp", FALLBACK.continueWhatsapp),
    clearDraft: pick("clearDraft", FALLBACK.clearDraft),
    back: pick("back", FALLBACK.back),
    skip: pick("skip", FALLBACK.skip),
    next: pick("next", FALLBACK.next),
    submit: pick("submit", FALLBACK.submit),
    newInquiry: pick("newInquiry", FALLBACK.newInquiry),
    retry: pick("retry", FALLBACK.retry),
    returnReview: pick("returnReview", FALLBACK.returnReview),
    promptName: pick("promptName", FALLBACK.promptName),
    promptWhatsapp: pick("promptWhatsapp", FALLBACK.promptWhatsapp),
    promptEmail: pick("promptEmail", FALLBACK.promptEmail),
    promptFinancing: pick("promptFinancing", FALLBACK.promptFinancing),
    promptDownPayment: pick("promptDownPayment", FALLBACK.promptDownPayment),
    promptLoanTerm: pick("promptLoanTerm", FALLBACK.promptLoanTerm),
    promptNotes: pick("promptNotes", FALLBACK.promptNotes),
    promptReview: pick("promptReview", FALLBACK.promptReview),
    placeholderName: pick("placeholderName", FALLBACK.placeholderName),
    placeholderWhatsapp: pick("placeholderWhatsapp", FALLBACK.placeholderWhatsapp),
    placeholderEmail: pick("placeholderEmail", FALLBACK.placeholderEmail),
    placeholderDownPayment: pick("placeholderDownPayment", FALLBACK.placeholderDownPayment),
    placeholderNotes: pick("placeholderNotes", FALLBACK.placeholderNotes),
    labelName: pick("labelName", FALLBACK.labelName),
    labelWhatsapp: pick("labelWhatsapp", FALLBACK.labelWhatsapp),
    labelEmail: pick("labelEmail", FALLBACK.labelEmail),
    labelDownPayment: pick("labelDownPayment", FALLBACK.labelDownPayment),
    labelLoanTerm: pick("labelLoanTerm", FALLBACK.labelLoanTerm),
    labelNotes: pick("labelNotes", FALLBACK.labelNotes),
    labelFinancing: pick("labelFinancing", FALLBACK.labelFinancing),
    labelVehicle: pick("labelVehicle", FALLBACK.labelVehicle),
    labelTermMonths: (months) =>
      pick("labelTermMonths", FALLBACK.labelTermMonths, { months }).replace(
        "{months}",
        String(months)
      ),
    financingYes: pick("financingYes", FALLBACK.financingYes),
    financingNo: pick("financingNo", FALLBACK.financingNo),
    financingUnsure: pick("financingUnsure", FALLBACK.financingUnsure),
    consentIntro: pick("consentIntro", FALLBACK.consentIntro),
    consentCheckbox: pick("consentCheckbox", FALLBACK.consentCheckbox),
    consentRequired: pick("consentRequired", FALLBACK.consentRequired),
    privacyPolicyLink: pick("privacyPolicyLink", FALLBACK.privacyPolicyLink),
    termsLink: pick("termsLink", FALLBACK.termsLink),
    legalLinksSeparator: pick("legalLinksSeparator", FALLBACK.legalLinksSeparator),
    continueWhatsappNote: pick("continueWhatsappNote", FALLBACK.continueWhatsappNote),
    submitting: pick("submitting", FALLBACK.submitting),
    submittingHint: pick("submittingHint", FALLBACK.submittingHint),
    success: pick("success", FALLBACK.success),
    error: pick("error", FALLBACK.error),
    draftRestore: pick("draftRestore", FALLBACK.draftRestore),
    recentStatus: pick("recentStatus", FALLBACK.recentStatus),
    prefillHelper: pick("prefillHelper", FALLBACK.prefillHelper),
    vehicleSwitched: pick("vehicleSwitched", FALLBACK.vehicleSwitched),
    dismiss: pick("dismiss", FALLBACK.dismiss),
    chatIntro: pick("chatIntro", FALLBACK.chatIntro),
    chatBotIntro: pick("chatBotIntro", FALLBACK.chatBotIntro),
    chatSyncHelper: pick("chatSyncHelper", FALLBACK.chatSyncHelper),
    chatDelayHelper: pick("chatDelayHelper", FALLBACK.chatDelayHelper),
    chatContinueFallback: pick("chatContinueFallback", FALLBACK.chatContinueFallback),
    chatFormFallback: pick("chatFormFallback", FALLBACK.chatFormFallback),
    chatPlaceholder: pick("chatPlaceholder", FALLBACK.chatPlaceholder),
    chatSend: pick("chatSend", FALLBACK.chatSend),
    chatSending: pick("chatSending", FALLBACK.chatSending),
    chatStoredNotice: pick("chatStoredNotice", FALLBACK.chatStoredNotice),
    chatSyncPendingNotice: pick("chatSyncPendingNotice", FALLBACK.chatSyncPendingNotice),
    chatOpenSentNotice: pick("chatOpenSentNotice", FALLBACK.chatOpenSentNotice),
    chatConsentIntro: pick("chatConsentIntro", FALLBACK.chatConsentIntro),
    chatLoadError: pick("chatLoadError", FALLBACK.chatLoadError),
    chatSendError: pick("chatSendError", FALLBACK.chatSendError),
    chatEmptyBody: pick("chatEmptyBody", FALLBACK.chatEmptyBody),
    chatLoading: pick("chatLoading", FALLBACK.chatLoading),
    channelWhatsapp: pick("channelWhatsapp", FALLBACK.channelWhatsapp),
    channelWeb: pick("channelWeb", FALLBACK.channelWeb),
    channelBot: pick("channelBot", FALLBACK.channelBot),
    statusRefresh: pick("statusRefresh", FALLBACK.statusRefresh),
    validation: {
      nameRequired: pick("validationNameRequired", FALLBACK.validationNameRequired),
      nameShort: pick("validationNameShort", FALLBACK.validationNameShort),
      whatsappRequired: pick("validationWhatsappRequired", FALLBACK.validationWhatsappRequired),
      whatsappInvalid: pick("validationWhatsappInvalid", FALLBACK.validationWhatsappInvalid),
      emailInvalid: pick("validationEmailInvalid", FALLBACK.validationEmailInvalid),
      financingRequired: pick("validationFinancingRequired", FALLBACK.validationFinancingRequired),
      downPaymentInvalid: pick(
        "validationDownPaymentInvalid",
        FALLBACK.validationDownPaymentInvalid
      ),
      downPaymentNegative: pick(
        "validationDownPaymentNegative",
        FALLBACK.validationDownPaymentNegative
      ),
      loanTermInvalid: pick("validationLoanTermInvalid", FALLBACK.validationLoanTermInvalid),
    },
    handoff: {
      success: pick("handoffSuccess", FALLBACK.handoffSuccess),
      error: pick("handoffError", FALLBACK.handoffError),
      vehicleInterest: pick("handoffVehicleInterest", FALLBACK.handoffVehicleInterest),
      general: pick("handoffGeneral", FALLBACK.handoffGeneral),
      price: pick("handoffPrice", FALLBACK.handoffPrice),
      location: pick("handoffLocation", FALLBACK.handoffLocation),
      listing: pick("handoffListing", FALLBACK.handoffListing),
      myDetails: pick("handoffMyDetails", FALLBACK.handoffMyDetails),
      financing: pick("handoffFinancing", FALLBACK.handoffFinancing),
      interest: pick("handoffInterest", FALLBACK.handoffInterest),
      downPayment: pick("handoffDownPayment", FALLBACK.handoffDownPayment),
      term: pick("handoffTerm", FALLBACK.handoffTerm),
      notes: pick("handoffNotes", FALLBACK.handoffNotes),
      reference: pick("handoffReference", FALLBACK.handoffReference),
      termMonths: (months) =>
        pick("handoffTermMonths", FALLBACK.handoffTermMonths, { months }).replace(
          "{months}",
          String(months)
        ),
    },
    financingOptions: [
      { value: "yes", label: pick("financingYes", FALLBACK.financingYes) },
      { value: "no", label: pick("financingNo", FALLBACK.financingNo) },
      { value: "unsure", label: pick("financingUnsure", FALLBACK.financingUnsure) },
    ],
    status: {
      submitted: {
        title: pick("statusSubmittedTitle", FALLBACK.statusSubmittedTitle),
        description: pick("statusSubmittedDescription", FALLBACK.statusSubmittedDescription),
      },
      broker_submitted: {
        title: pick("statusBrokerSubmittedTitle", FALLBACK.statusBrokerSubmittedTitle),
        description: pick(
          "statusBrokerSubmittedDescription",
          FALLBACK.statusBrokerSubmittedDescription
        ),
      },
      confirming: {
        title: pick("statusConfirmingTitle", FALLBACK.statusConfirmingTitle),
        description: pick("statusConfirmingDescription", FALLBACK.statusConfirmingDescription),
      },
      waiting_for_dealer: {
        title: pick("statusWaitingForDealerTitle", FALLBACK.statusWaitingForDealerTitle),
        description: pick(
          "statusWaitingForDealerDescription",
          FALLBACK.statusWaitingForDealerDescription
        ),
      },
      dealer_accepted: {
        title: pick("statusDealerAcceptedTitle", FALLBACK.statusDealerAcceptedTitle),
        description: pick(
          "statusDealerAcceptedDescription",
          FALLBACK.statusDealerAcceptedDescription
        ),
      },
      handoff_complete: {
        title: pick("statusHandoffCompleteTitle", FALLBACK.statusHandoffCompleteTitle),
        description: pick(
          "statusHandoffCompleteDescription",
          FALLBACK.statusHandoffCompleteDescription
        ),
      },
      partial_success: {
        title: pick("statusPartialSuccessTitle", FALLBACK.statusPartialSuccessTitle),
        description: pick(
          "statusPartialSuccessDescription",
          FALLBACK.statusPartialSuccessDescription
        ),
      },
      failed: {
        title: pick("statusFailedTitle", FALLBACK.statusFailedTitle),
        description: pick("statusFailedDescription", FALLBACK.statusFailedDescription),
      },
      submitting: {
        title: pick("statusSubmittingTitle", FALLBACK.statusSubmittingTitle),
        description: pick("statusSubmittingDescription", FALLBACK.statusSubmittingDescription),
      },
    },
    privacyAccountPreferencesApply: pick(
      "privacyAccountPreferencesApply",
      FALLBACK.privacyAccountPreferencesApply
    ),
    privacyReviewSettings: pick("privacyReviewSettings", FALLBACK.privacyReviewSettings),
    privacyReviewRequired: pick("privacyReviewRequired", FALLBACK.privacyReviewRequired),
    privacyRequiredBeforeLead: pick(
      "privacyRequiredBeforeLead",
      FALLBACK.privacyRequiredBeforeLead
    ),
    privacyContinueChatting: pick("privacyContinueChatting", FALLBACK.privacyContinueChatting),
    privacyDealerSharingDisabled: pick(
      "privacyDealerSharingDisabled",
      FALLBACK.privacyDealerSharingDisabled
    ),
    privacyHistoryNotSaved: pick("privacyHistoryNotSaved", FALLBACK.privacyHistoryNotSaved),
    privacySettingsUpdated: pick("privacySettingsUpdated", FALLBACK.privacySettingsUpdated),
    privacyContinuePendingAction: pick(
      "privacyContinuePendingAction",
      FALLBACK.privacyContinuePendingAction
    ),
    privacyComplianceUnavailable: pick(
      "privacyComplianceUnavailable",
      FALLBACK.privacyComplianceUnavailable
    ),
    privacyRetry: pick("privacyRetry", FALLBACK.privacyRetry),
    privacyReturnToChat: pick("privacyReturnToChat", FALLBACK.privacyReturnToChat),
    privacyReviewTitle: pick("privacyReviewTitle", FALLBACK.privacyReviewTitle),
    privacyNotNow: pick("privacyNotNow", FALLBACK.privacyNotNow),
    guestPrivacyNotice: pick("guestPrivacyNotice", FALLBACK.guestPrivacyNotice),
    guestLeadTitle: pick("guestLeadTitle", FALLBACK.guestLeadTitle),
    guestLeadNotice: pick("guestLeadNotice", FALLBACK.guestLeadNotice),
    guestContinueAsGuest: pick("guestContinueAsGuest", FALLBACK.guestContinueAsGuest),
    guestCreateAccount: pick("guestCreateAccount", FALLBACK.guestCreateAccount),
    guestCancel: pick("guestCancel", FALLBACK.guestCancel),
    guestCreateAccountTitle: pick("guestCreateAccountTitle", FALLBACK.guestCreateAccountTitle),
    guestCreateAccountDescription: pick(
      "guestCreateAccountDescription",
      FALLBACK.guestCreateAccountDescription
    ),
    guestEmailLabel: pick("guestEmailLabel", FALLBACK.guestEmailLabel),
    guestContinueButton: pick("guestContinueButton", FALLBACK.guestContinueButton),
    guestCheckEmailTitle: pick("guestCheckEmailTitle", FALLBACK.guestCheckEmailTitle),
    guestCheckEmailDescription: pick(
      "guestCheckEmailDescription",
      FALLBACK.guestCheckEmailDescription
    ),
    guestNewAccountAgreementsTitle: pick(
      "guestNewAccountAgreementsTitle",
      FALLBACK.guestNewAccountAgreementsTitle
    ),
    guestTermsAcceptance: pick("guestTermsAcceptance", FALLBACK.guestTermsAcceptance),
    guestDataAuthorization: pick("guestDataAuthorization", FALLBACK.guestDataAuthorization),
    guestExistingAccountTitle: pick(
      "guestExistingAccountTitle",
      FALLBACK.guestExistingAccountTitle
    ),
    guestExistingAccountDescription: pick(
      "guestExistingAccountDescription",
      FALLBACK.guestExistingAccountDescription
    ),
    guestSignIn: pick("guestSignIn", FALLBACK.guestSignIn),
    guestPasswordLabel: pick("guestPasswordLabel", FALLBACK.guestPasswordLabel),
    guestConsentFailed: pick("guestConsentFailed", FALLBACK.guestConsentFailed),
  };
}

export function useAutomapWhatsappWidgetCopy() {
  const { t, locale, language } = useLocale();
  const currency = useOptionalCurrency();
  const copy = useMemo(() => buildAutomapWhatsappWidgetCopy(t), [t]);
  return {
    copy,
    locale,
    language,
    displayCurrency: currency?.displayCurrency || "COP",
    formatMoney: currency?.formatMoney || null,
    rates: currency?.rates || null,
    ratesAvailable: currency?.ratesAvailable,
  };
}

/** Safe when widget mounts outside IntlProvider (tests / edge cases). */
export function useOptionalAutomapWhatsappWidgetCopy() {
  const localeCtx = useOptionalLocale();
  const currency = useOptionalCurrency();
  const t = localeCtx?.t || ((key) => key);
  const copy = useMemo(() => buildAutomapWhatsappWidgetCopy(t), [t]);
  return {
    copy,
    locale: localeCtx?.locale || "es-CO",
    language: localeCtx?.language || "es",
    displayCurrency: currency?.displayCurrency || "COP",
    formatMoney: currency?.formatMoney || null,
    rates: currency?.rates || null,
    ratesAvailable: currency?.ratesAvailable,
  };
}

export { FALLBACK as AUTOMAP_WHATSAPP_WIDGET_COPY_FALLBACK };
